/**
 * AR Drop Alerts — two-step Klaviyo signup for the drops page.
 *
 * Step 1: email -> Klaviyo client subscription, EMAIL marketing only.
 * Step 2: optional mobile number -> Klaviyo client subscription, SMS marketing
 *         only (explicit consent: the disclosure sits above the button).
 * Both add the profile to the list in data-list-id and record data-source
 * as the consent "custom source", so signups can be traced to this page.
 *
 * When a subscriptions object names channels, Klaviyo subscribes only those
 * channels, which is why each step names exactly one.
 */
(function () {
  const ENDPOINT = "https://a.klaviyo.com/client/subscriptions/";
  const REVISION = "2026-01-15";
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // US numbers only (the page shows a fixed +1). Accepts 10 digits, or 11
  // starting with 1; returns E.164 (+1XXXXXXXXXX) or null.
  function toE164(raw) {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.charAt(0) === "1") return `+${digits}`;
    return null;
  }

  function subscribe(config, attributes, channel) {
    const subscriptions = {};
    subscriptions[channel] = { marketing: { consent: "SUBSCRIBED" } };

    const body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: config.source || undefined,
          profile: {
            data: {
              type: "profile",
              attributes: Object.assign({}, attributes, { subscriptions }),
            },
          },
        },
        relationships: {
          list: { data: { type: "list", id: config.listId } },
        },
      },
    };

    return fetch(`${ENDPOINT}?company_id=${encodeURIComponent(config.companyId)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        Accept: "application/vnd.api+json",
        revision: REVISION,
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
      return true;
    });
  }

  function init(root) {
    if (!root || root.dataset.arReady === "true") return;
    root.dataset.arReady = "true";

    const config = {
      companyId: root.dataset.companyId,
      listId: root.dataset.listId,
      source: root.dataset.source,
    };
    const steps = {
      email: root.querySelector('[data-ar-step="email"]'),
      sms: root.querySelector('[data-ar-step="sms"]'),
      done: root.querySelector('[data-ar-step="done"]'),
    };
    let savedEmail = "";

    function show(name) {
      Object.keys(steps).forEach((key) => {
        if (steps[key]) steps[key].hidden = key !== name;
      });
      const focusTarget = steps[name] && steps[name].querySelector("[data-ar-focus]");
      if (focusTarget) focusTarget.focus({ preventScroll: false });
      root.scrollIntoView({ block: "start", behavior: "smooth" });
    }

    function setBusy(form, busy) {
      const button = form.querySelector("[data-ar-submit]");
      if (!button) return;
      if (busy) {
        button.dataset.label = button.textContent;
        button.textContent = "One sec…";
      } else if (button.dataset.label) {
        button.textContent = button.dataset.label;
      }
      button.disabled = busy;
    }

    function setError(form, input, message) {
      const error = form.querySelector("[data-ar-error]");
      if (error) {
        error.textContent = message || "";
        error.hidden = !message;
      }
      if (input) {
        if (message) {
          input.setAttribute("aria-invalid", "true");
          if (error) {
            error.id = error.id || `${input.id}-error`;
            input.setAttribute("aria-describedby", error.id);
          }
        } else {
          input.removeAttribute("aria-invalid");
          input.removeAttribute("aria-describedby");
        }
      }
    }

    const emailForm = root.querySelector("[data-ar-email-form]");
    if (emailForm) {
      emailForm.addEventListener("submit", (event) => {
        event.preventDefault();
        const input = emailForm.querySelector('input[name="email"]');
        const email = (input.value || "").trim();

        if (!EMAIL_PATTERN.test(email)) {
          setError(emailForm, input, "Enter a valid email address.");
          input.focus();
          return;
        }
        if (!config.companyId || !config.listId) {
          setError(emailForm, input, "Signup isn't set up yet. Please try again later.");
          return;
        }

        setError(emailForm, input, "");
        setBusy(emailForm, true);
        subscribe(config, { email }, "email")
          .then(() => {
            savedEmail = email;
            show("sms");
          })
          .catch(() => {
            setError(emailForm, input, "Something went wrong. Please try again.");
          })
          .finally(() => setBusy(emailForm, false));
      });
    }

    const smsForm = root.querySelector("[data-ar-sms-form]");
    if (smsForm) {
      smsForm.addEventListener("submit", (event) => {
        event.preventDefault();
        const input = smsForm.querySelector('input[name="phone"]');
        const phone = toE164(input.value);

        if (!phone) {
          setError(smsForm, input, "Enter a 10-digit US mobile number.");
          input.focus();
          return;
        }

        setError(smsForm, input, "");
        setBusy(smsForm, true);
        const attributes = { phone_number: phone };
        if (savedEmail) attributes.email = savedEmail;
        subscribe(config, attributes, "sms")
          .then(() => show("done"))
          .catch(() => {
            setError(smsForm, input, "We couldn't add that number. Check it and try again.");
          })
          .finally(() => setBusy(smsForm, false));
      });

      const skip = smsForm.querySelector("[data-ar-skip]");
      if (skip) skip.addEventListener("click", () => show("done"));
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-ar-drop-alerts]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
